import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Building2 } from "lucide-react";
import OrgLookupButton from "./OrgLookupButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ company_name: "", org_number: "", contact_name: "", email: "", phone: "", billing_email: "", country: "SE", vat_number: "", status: "active", services: [] });
  const navigate = useNavigate();

  const fetchCustomers = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/customers`, { withCredentials: true });
    setCustomers(data);
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const filtered = customers.filter(c =>
    !search || c.company_name?.toLowerCase().includes(search.toLowerCase()) || c.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  const createCustomer = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/customers`, form, { withCredentials: true });
    setShowCreate(false);
    setForm({ company_name: "", org_number: "", contact_name: "", email: "", phone: "", billing_email: "", status: "active", services: [] });
    fetchCustomers();
  };

  const statusBadge = (s) => {
    const map = { active: "bg-green-500/10 text-green-400", inactive: "bg-neutral-500/10 text-[#857973]", churned: "bg-red-500/10 text-red-400" };
    return map[s] || "bg-[#1E3050]/40 text-[#857973]";
  };

  return (
    <div data-testid="customers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Customers</h1>
          <p className="text-sm text-[#857973]">{customers.length} customers</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button data-testid="create-customer-btn" className="flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all">
              <Plus className="w-4 h-4" /> New Customer
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F1F3A] border-[#1E3050] text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-outfit">Create Customer</DialogTitle></DialogHeader>
            <form onSubmit={createCustomer} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Company name" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-customer-company" />
                <input placeholder="Org number" value={form.org_number} onChange={e => setForm(p => ({ ...p, org_number: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <OrgLookupButton orgnr={form.org_number} label="Auto-fill from org number"
                onResult={d => setForm(p => ({
                  ...p,
                  company_name: p.company_name || d.company_name,
                  org_number: d.orgnr || p.org_number,
                  vat_number: p.vat_number || d.vat_number,
                  country: d.country || p.country,
                  billing_email: p.billing_email,
                }))} />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Contact name" value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-customer-contact" />
                <input required placeholder="Email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-customer-email" />
              </div>
              <input placeholder="Phone" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              <input placeholder="Billing email" value={form.billing_email} onChange={e => setForm(p => ({ ...p, billing_email: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" data-testid="create-customer-country">
                  <option value="SE">Sweden</option><option value="NO">Norway</option><option value="DK">Denmark</option><option value="FI">Finland</option><option value="EE">Estonia</option><option value="DE">Germany</option><option value="GB">United Kingdom</option><option value="US">United States</option><option value="OTHER">Other</option>
                </select>
                <input placeholder="VAT number" value={form.vat_number} onChange={e => setForm(p => ({ ...p, vat_number: e.target.value }))} className="bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
              </div>
              <button type="submit" data-testid="create-customer-submit" className="w-full bg-[#F59073] text-black py-2.5 rounded-sm text-sm font-medium">Create Customer</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
        <input data-testid="customers-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers..."
          className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(c => (
          <div key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)} data-testid={`customer-card-${c.id}`}
            className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-5 cursor-pointer hover:border-[#1E3050] transition-all group">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#F59073]/10 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#F59073]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-outfit font-medium text-white text-sm truncate">{c.company_name}</h3>
                <p className="text-xs text-[#857973] truncate">{c.contact_name}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusBadge(c.status)}`}>{c.status}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-[#857973]">
              <span>{c.email}</span>
              <span>{c.org_number || "—"}</span>
            </div>
            {c.services?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {c.services.map((s, i) => <span key={i} className="text-xs bg-[#1E3050]/40 text-[#857973] px-2 py-0.5 rounded-sm">{s}</span>)}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-[#857973] col-span-3 text-center py-12">No customers found</p>}
      </div>
    </div>
  );
}
